// src/components/personal/sections/OrdersSection/components/Table.tsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Order } from '../types/types';
import { getOrderStatusColor, getOrderStatusTextStatic, getSalesChannelTextStatic } from '../utils/statusHelpers';
import { TEXT_STYLES } from '@/theme/typography';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatDateTime } from '@/utils/dateUtils';

// Используем оригинальный UITable вместо создания своей реализации
import { Table as UITable, TableColumn } from '@/components/ui/Table/Table';

interface TableProps {
  filteredOrders: Order[];
  onOrderSelect: (orderId: string) => void;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  loading?: boolean;
  error?: Error | null;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  scrollable?: boolean;
  autoHeight?: boolean;
  totalItems: number;
  totalPages: number;
}

const Table: React.FC<TableProps> = ({
  filteredOrders,
  onOrderSelect,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  columnWidths,
  onColumnWidthsChange,
  sortColumn,
  sortDirection,
  onSort,
  scrollable = false,
  autoHeight = true,
  totalItems,
  totalPages
}) => {
  const { t } = useTranslation(['orders', 'common', 'errors']);
  const { formatPrice } = useBusinessCurrency();

  // Для отладки параметров пагинации
  console.log('OrderTable - Параметры пагинации:', {
    ordersCount: filteredOrders.length,
    currentPage,
    pageSize,
    totalItems,
    totalPages
  });

  // Определение колонок таблицы
  const columns: TableColumn<Order>[] = useMemo(() => [
    { 
      key: 'id', 
      title: t('table.id'), 
      width: 'xs',
      resizable: true,
      render: (order) => (
        <div className="flex items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOrderSelect(order.id);
            }}
            className="text-primary hover:text-primary/80 transition-colors duration-200 mr-2"
            title={t('table.open_card')}
          >
            <FileText className="w-4 h-4" />
          </button>
          <div title={t('table.id_with_tooltip', { id: order.id })}>
            {order.id}
          </div>
        </div>
      ),
      mobilePriority: 0 // Самый высокий приоритет
    },
    { 
      key: 'client_name', 
      title: t('table.client'), 
      width: 'md',
      resizable: true,
      truncate: true,
      maxLength: 30,
      sortable: true,
      render: (order) => (
        <div>
          <div className="truncate" title={order.client_name || t('table.unknown_client')}>
            {order.client_name || t('table.unknown_client')}
          </div>
          {order.client_phone && (
            <div className="text-xs text-gray-500 truncate" title={order.client_phone}>
              {order.client_phone}
            </div>
          )}
        </div>
      ),
      mobilePriority: 1
    },
    { 
      key: 'status', 
      title: t('table.status'), 
      width: 'sm',
      resizable: true,
      sortable: true,
      render: (order) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${TEXT_STYLES.tiny} ${getOrderStatusColor(order.status)}`}>
          {getOrderStatusTextStatic(order.status, t)}
        </span>
      ),
      mobilePriority: 2
    },
    { 
      key: 'total_cost', 
      title: t('table.total'), 
      width: 'sm',
      resizable: true,
      textAlign: 'right',
      sortable: true,
      render: (order) => {
        const totalValue = 
          typeof order.total_cost === 'string' 
            ? parseFloat(order.total_cost.replace(/[^\d.-]/g, '')) || 0
            : (order.cost_data?.total || 0);
        
        return (
          <span className="font-medium">
            {formatPrice(totalValue)}
          </span>
        );
      },
      mobilePriority: 2
    },
    { 
      key: 'created_at', 
      title: t('table.date'),
      width: 'md',
      resizable: true,
      sortable: true,
      render: (order) => (
        <span>
          {order.created_at ? formatDateTime(order.created_at, false, t('common:locale')) : '—'}
        </span>
      ),
      mobilePriority: 3
    },
    { 
      key: 'platform', 
      title: t('table.platform'),
      width: 'sm',
      resizable: true,
      truncate: true,
      sortable: true,
      render: (order) => (
        <span className="truncate" title={getSalesChannelTextStatic(order.platform, t)}>
          {getSalesChannelTextStatic(order.platform, t)}
        </span>
      ),
      mobilePriority: 4
    }
  ], [t, onOrderSelect, formatPrice]);

  // ИСПРАВЛЕНО: Изменены параметры передачи для UITable
  const handlePageChangeProxy = (page: number) => {
    console.log(`Table - Вызван handlePageChange с параметром: ${page}`);
    onPageChange(page);
  };

  const handlePageSizeChangeProxy = (size: number) => {
    console.log(`Table - Вызван handlePageSizeChange с параметром: ${size}`);
    onPageSizeChange(size);
  };

  return (
    <div className="w-full">
      <UITable
        data={filteredOrders}
        columns={columns}
        onRowClick={(order) => onOrderSelect(order.id)}
        isLoading={loading}
        emptyText={t('orders:no_filtered_results')}
        loadingText={t('common:loading')}
        keyExtractor={(order) => order.id}
        scrollable={scrollable}
        autoHeight={autoHeight}
        
        // Параметры для сортировки
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        
        // ИСПРАВЛЕНО: Улучшен подход к пагинации
        showPagination={false}
        
        // Параметры для пагинации
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChangeProxy}
        onPageSizeChange={handlePageSizeChangeProxy}
        
        // Параметры для изменяемых размеров столбцов
        resizableColumns={true}
        savedColumnWidths={columnWidths}
        onColumnWidthsChange={onColumnWidthsChange}
        
        // Адаптивность
        responsive={true}
        mobileBehavior="cards"
        className="w-full"
      />
      
      {/* ВАЖНО: Пагинация вынесена в OrdersSection */}
    </div>
  );
};

export default React.memo(Table);